import React from 'react';
import styled from 'styled-components';
import { NextPage, GetServerSideProps, GetServerSidePropsContext } from 'next';
import Image from 'next/image';
import posthog from 'posthog-js';
import Container from '../components/Container';
import Head from '../components/Head';

import { getCollectionByHandle, getCollectionByHandleDetailed } from '../api/collections';
import { getEndingSoonProducts } from '../utils/collections';
import { logSentryError } from '../utils/errors';
import config from '../../config.json';
import Masthead from '../components/pages/home/Masthead';
import { isUserLoggedIn } from '../utils/analytics';
import { useStore } from '../store';
import { getPropsForPageLoad } from '../utils/pageLoad';
import { ProductTag } from '../types/common';
import FeaturedCreatorsListContainer from '../components/pages/home/FeaturedCreatorsList/FeaturedCreatorsListContainer';
import { loadFeaturedCollections } from '../services/featuredCreatorsService';
import { ButtonPrimary } from '../ui/atoms/Button';
import SubscribeSectionContainer from '../components/pages/home/SubscribeSection/SubscribeSection';
import ProductListContainer from '../components/pages/home/ProductList/ProductListContainer';
import LaunchCampaignCTASection from '../components/pages/home/LaunchCampaignCTASection';

const { collections } = config;

const HomeContainer = styled(Container)`
  padding: 0 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: 0 16px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    padding: 0 64px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.maxContent}px) {
    padding: 0 auto;
  }
`;

const FeatureWrapper = styled.div`
  display: flex;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin: 0 8px;
  }
`;

const ProductFeature = styled.div<{ first: boolean }>`
  position: relative;
  width: 640px;
  height: 640px;
  margin: 32px 0;
  ${({ first }) => !first && `margin-left: 32px;`}
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  cursor: pointer;
  align-items: center;

  @media screen and (max-width: ${({ theme }) => theme.containerWidth}px) {
    height: calc(50vw - 80px);
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    margin: 16px 0;
    width: 100%;
    height: calc(100vw - 32px);
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    height: calc(100vw - 16px);
  }
`;

type HomePageProps = {
  masthead: Shopify.Collection | null;
  manualMasthead: Shopify.Collection | null;
  liveCampaigns: Shopify.ProductEdge[] | null;
  endingSoon: Shopify.ProductEdge[] | null;
  comingSoon: Shopify.ProductEdge[] | null;
  petitions: Shopify.ProductEdge[] | null;
  features: (Shopify.Collection | null)[];
  featuredCreators: (MastheadCarouselItemData | null)[];
  featuredCollections: CollectionEdgeResponse[] | null;
};

export type MastheadCarouselItemData = {
  descriptionText: string;
  descriptionTextColor: string;
  descriptionBackgroundColor: string;
  ctaText: string;
  ctaBackgroundColor: string;
  ctaLink: string;
  collectionName: string;
  productImage: string;
  logoImage: string;
  backgroundImage: string;
  collectionHandle: string;
};

const HomePage: NextPage<HomePageProps> = ({
  masthead,
  manualMasthead,
  liveCampaigns,
  endingSoon,
  comingSoon,
  features,
  petitions,
  featuredCreators,
  featuredCollections,
}: HomePageProps) => {
  const { state } = useStore();

  return (
    <>
      <Head title="Makeship" url="/" hideName />
      <Masthead masthead={masthead} manualMasthead={manualMasthead} featuredCreators={featuredCreators} />
      <FeaturedCreatorsListContainer featuredCollections={featuredCollections} />
      <ProductListContainer
        products={liveCampaigns}
        sectionTitle="Live Campaigns"
        href={`/shop/${collections.liveCampaigns}`}
        titleForTracking="Live Campaigns"
        filterType="liveCampaigns"
      />
      <ProductListContainer
        products={petitions}
        sectionTitle="Live Petitions"
        href={`/shop/${collections.petitions}`}
        titleForTracking="Live Petitions"
        filterType="petitions"
      />
      <HomeContainer>
        {features && (
          <FeatureWrapper>
            {features.map(
              (feature, index) =>
                feature && (
                  <ProductFeature
                    first={index === 0}
                    onClick={() =>
                      posthog.capture('home_exploreFeature_click', {
                        link_handle: feature.handle,
                        link_href: `/shop/${feature.handle}`,
                        is_logged_in: isUserLoggedIn(state.user),
                      })
                    }
                  >
                    <Image
                      layout="fill"
                      objectFit="contain"
                      src={feature.image?.transformedSrc}
                      role="presentation"
                      alt=""
                      quality={90}
                      unoptimized
                    />
                    <ButtonPrimary className="z-1 mb-3.5" href={`/shop/${feature.handle}`}>
                      Explore {feature.title}
                    </ButtonPrimary>
                  </ProductFeature>
                ),
            )}
          </FeatureWrapper>
        )}
      </HomeContainer>
      <ProductListContainer
        products={endingSoon}
        sectionTitle="Ending Soon"
        href="/shop/ending-soon"
        titleForTracking="Ending Soon"
        filterType="endingSoon"
      />
      <ProductListContainer
        products={comingSoon}
        sectionTitle="Coming Soon"
        href={`/shop/${collections.comingSoon}`}
        titleForTracking="Coming Soon"
        filterType="comingSoon"
      />
      <LaunchCampaignCTASection />
      <SubscribeSectionContainer />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<HomePageProps> = async (ctx: GetServerSidePropsContext) => {
  const { pagination } = config;
  const propsForPageLoad = await getPropsForPageLoad(ctx);

  type MastheadCarouselItemData = {
    descriptionText: string;
    descriptionTextColor: string;
    descriptionBackgroundColor: string;
    ctaText: string;
    ctaBackgroundColor: string;
    ctaTextColor: string;
    ctaLink: string;
    collectionName: string;
    productImage: string;
    logoImage: string;
    backgroundImage: string;
    collectionHandle: string;
  };

  const mastheadCarouselItems: MastheadCarouselItemData[] = [
    // Example of a featured creator item
    // {
    //   descriptionText: 'Shop the newest drop from UltraKill! Only here for a limited time.',
    //   descriptionTextColor: 'white',
    //   descriptionBackgroundColor: 'black',
    //   ctaText: 'Shop UltraKill',
    //   ctaBackgroundColor: 'white',
    //   ctaLink: '/shop/creator/ultrakill',
    //   collectionName: 'UltraKill',
    //   productImage: '/assets/featured-creators/ultrakill/products.png',
    //   logoImage: '/assets/featured-creators/ultrakill/logo.png',
    //   backgroundImage: '/assets/featured-creators/ultrakill/background.png',
    //   collectionHandle: 'ultrakill',
    // },
    {
      descriptionText: 'Shop the newest limited-edition drops from Invincible!',
      descriptionTextColor: 'black',
      descriptionBackgroundColor: 'white',
      ctaText: 'Shop Invincible',
      ctaBackgroundColor: 'black',
      ctaTextColor: 'white',
      ctaLink: '/shop/creator/invincible',
      collectionName: 'Invincible',
      productImage: '/assets/featured-creators/invincible/products.png',
      logoImage: '/assets/featured-creators/invincible/logo-2.png',
      backgroundImage: '/assets/featured-creators/invincible/background.png',
      collectionHandle: 'invincible',
    },
    {
      descriptionText: 'Shop the newest drop from Godot! Only available for a limited time.',
      descriptionTextColor: 'black',
      descriptionBackgroundColor: 'white',
      ctaText: 'Shop Now',
      ctaBackgroundColor: 'black',
      ctaTextColor: 'white',
      ctaLink: '/products/godot-robot-v2-plushie',
      collectionName: 'Godot Engine',
      productImage: '/assets/featured-creators/godot/Godot_Engine_Godot_Robot_Masthead_Product.png',
      logoImage: '/assets/featured-creators/godot/Godot_Fondation_Logo_Website.png',
      backgroundImage: '/assets/featured-creators/godot/Godot_Engine_Godot_Robot_Masthead_BG.png',
      collectionHandle: 'godot',
    },
  ];

  const results = await Promise.allSettled([
    getCollectionByHandleDetailed(collections.automatedMasthead, undefined, pagination.automatedMasthead),
    getCollectionByHandleDetailed(collections.masthead, undefined, pagination.manualMasthead),
    getCollectionByHandle(collections.liveCampaigns).then(
      (collection) => (collection?.products?.edges as Shopify.ProductEdge[]) || null,
    ),
    getCollectionByHandle(collections.comingSoon).then(
      (collection) => (collection?.products?.edges as Shopify.ProductEdge[]) || null,
    ),
    getEndingSoonProducts(collections.liveCampaigns),
    getCollectionByHandle(collections.petitions).then(
      (collection) => (collection?.products?.edges as Shopify.ProductEdge[]) || null,
    ),
    getCollectionByHandle(collections.plushies),
    getCollectionByHandle(collections.topCampaigns),
    Promise.all(
      mastheadCarouselItems.map(async (item) => {
        try {
          const collection = await getCollectionByHandle(item.collectionHandle);
          if (collection?.products?.edges) {
            const hasLiveProduct = collection.products.edges.some((productEdge) =>
              productEdge.node?.tags?.includes(ProductTag.Live),
            );
            if (hasLiveProduct) {
              return item;
            }
          }
          return null;
        } catch (error) {
          logSentryError(
            error,
            'homepage_getServerSideProps',
            `Failed to get featured creator collection ${item.collectionHandle}`,
          );
          return null;
        }
      }),
    ).then((creatorsWithLiveProducts) => creatorsWithLiveProducts.filter(Boolean)),
    loadFeaturedCollections(),
  ]);

  const promiseConfig = [
    { name: 'masthead', errorMessage: 'Failed to get automated masthead collection', defaultValue: null },
    { name: 'manualMasthead', errorMessage: 'Failed to get manual masthead collection', defaultValue: null },
    { name: 'liveCampaigns', errorMessage: 'Failed to get live campaigns collection', defaultValue: null },
    { name: 'comingSoon', errorMessage: 'Failed to get coming soon collection', defaultValue: null },
    { name: 'endingSoon', errorMessage: 'Failed to get ending soon products', defaultValue: null },
    { name: 'petitions', errorMessage: 'Failed to get petitions collection', defaultValue: null },
    { name: 'plushies', errorMessage: 'Failed to get plushies collection', defaultValue: null },
    { name: 'topCampaigns', errorMessage: 'Failed to get top campaigns collection', defaultValue: null },
    { name: 'featuredCreators', errorMessage: 'Failed to get featured creators', defaultValue: [] },
    { name: 'featuredCollections', errorMessage: 'Failed to get featured collections', defaultValue: null },
  ];

  const [
    masthead,
    manualMasthead,
    liveCampaigns,
    comingSoon,
    endingSoon,
    petitions,
    plushies,
    topCampaigns,
    featuredCreators,
    featuredCollections,
  ] = results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    }

    logSentryError(result.reason, 'homepage_getServerSideProps', promiseConfig[index].errorMessage);

    return promiseConfig[index].defaultValue;
  }) as [
    Shopify.Collection | null,
    Shopify.Collection | null,
    Shopify.ProductEdge[] | null,
    Shopify.ProductEdge[] | null,
    Shopify.ProductEdge[] | null,
    Shopify.ProductEdge[] | null,
    Shopify.Collection | null,
    Shopify.Collection | null,
    (MastheadCarouselItemData | null)[],
    CollectionEdgeResponse[] | null,
  ];

  return {
    props: {
      masthead,
      manualMasthead,
      liveCampaigns,
      comingSoon,
      endingSoon,
      petitions,
      features: [plushies, topCampaigns],
      featuredCreators,
      featuredCollections,
      ...propsForPageLoad,
    },
  };
};

export default HomePage;
